import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, Inbox, Bot, LayoutGrid, Star, ShieldCheck, Clock, Users, ArrowRight, CalendarCheck, FileText, CheckCircle2, Check, TrendingDown, TrendingUp, PhoneCall, Zap, MessageSquare, Wrench } from 'lucide-react';
const BOOKING_URL = 'https://api.viridiaanalytics.com/widget/booking/hv5pIfS27zbJCMMGNnkJ';
const PHONE_DISPLAY = '(623) 300-0708';
const PHONE_TEL = 'tel:+16233000708';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/VA-Logo_(1).png';
const CHAMBER_BADGE = 'https://cdn.magicpatterns.com/uploads/8HQjTNAJm388sm9my7xsCp/Scotsscommerce.png';

// GHL inbound webhook -- primed by VEGA with a sample payload (HTTP 200, field
// mapping confirmed) for exactly the keys this form sends. Inbound webhooks
// don't take an API key; the URL itself is a write-only trigger endpoint, so
// it's fine in client source (2026-08-18 handoff).
const LEAD_WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/JLycxFbUVQFY3z4zPpCS/webhook-trigger/33bbaf7b-b55d-498b-bf1b-3f24efca5231';

/* Tracking split three ways (2026-08-18) -- the old single trackLead() fired on
   every CTA click, so Meta was optimizing toward "clicked something" instead of
   a real conversion. Only the webhook-confirmed form submit is a true Lead now;
   booking and phone intent get their own distinctly-named events -- matching
   FUNNEL-SPEC.md section 5's canonical names -- so Kevin can read the funnel
   honestly in Ads Manager. */
function trackWebhookLead() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('track', 'Lead');
  if (typeof w.gtag === 'function') w.gtag('event', 'generate_lead');
}
function trackBookingOpened() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('trackCustom', 'BookingOpened');
  if (typeof w.gtag === 'function') w.gtag('event', 'booking_click');
}
function trackCallTapped() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('trackCustom', 'CallTapped');
  if (typeof w.gtag === 'function') w.gtag('event', 'call_tapped');
}

/* A lead vanishing silently is worse than an error (2026-08-18 handoff). The
   visitor still sees success either way -- this is the "make it visible to
   Kevin" half: a distinct GA4 event that never fires on a real success, plus a
   local backup so the raw lead isn't lost even if nobody sees the console. */
function flagLeadDeliveryFailure(reason: string, lead: Record<string, string>) {
  console.error('[lead-form] delivery failed:', reason, lead);
  const w = window as any;
  if (typeof w.gtag === 'function') w.gtag('event', 'lead_delivery_failed', {
    reason
  });
  try {
    const key = 'va_failed_leads';
    const backup = JSON.parse(localStorage.getItem(key) || '[]');
    backup.push({
      ...lead,
      reason,
      ts: new Date().toISOString()
    });
    localStorage.setItem(key, JSON.stringify(backup));
  } catch {
    /* localStorage unavailable -- console.error above is the only record left */
  }
}
function usd(n: number) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

/* Shared scroll-reveal + reduced-motion primitives (2026-08-18, item 9/hard
   constraint: no animation library, no new dependency). Plain IntersectionObserver
   and matchMedia, a few lines each, reused wherever the page needs entrance motion
   -- the form, the case study cards, the lead-flow diagram. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}
function useInViewOnce(threshold = 0.2) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, {
      threshold
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, threshold]);
  return [ref, inView] as const;
}
function BookCTA({
  label = 'Book Your Free 15-Minute Call',
  full = false
}: {
  label?: string;
  full?: boolean;
}) {
  return <a href={BOOKING_URL} onClick={trackBookingOpened} className={`group inline-flex items-center justify-center gap-2 rounded-md bg-emerald-500 px-7 py-4 text-sm font-bold text-black transition-colors hover:bg-emerald-400 ${full ? 'w-full' : ''}`} data-id="element-0" data-mp="zw0ovh53sagy">
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-1" data-mp="3gkwe8wcpu9en" />
    </a>;
}

/* Calling is the lower-friction ask for this audience -- contractors reach for the
   phone before they fill in a form. The number is the GHL line, so the AI picks up:
   the call itself is a live demo of the product. Tap intent is real but isn't a
   confirmed lead, so it fires its own event, not Lead -- name per FUNNEL-SPEC.md
   section 5 (2026-08-18). */
function CallCTA({
  full = false
}: {
  full?: boolean;
}) {
  return <a href={PHONE_TEL} onClick={trackCallTapped} className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-emerald-500/60 bg-transparent px-5 py-4 text-sm font-bold tracking-tight text-emerald-300 transition-colors hover:bg-emerald-500/10 ${full ? 'w-full' : ''}`} data-id="element-2" data-mp="2jtzmn7hjsawv">
      <PhoneCall className="h-4 w-4 flex-none" data-id="element-3" data-mp="tid8zdd52zr8" />
      Call {PHONE_DISPLAY}
    </a>;
}
function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="font-eyebrow inline-block text-[11px] font-semibold uppercase text-emerald-500" data-id="element-4" data-mp="yihnwyyefmfc">{children}</div>;
}
function GuaranteeBadge({
  big = false
}: {
  big?: boolean;
}) {
  return <div className={`inline-flex items-center gap-2 rounded-full border-2 border-emerald-500/60 bg-emerald-500/15 font-extrabold uppercase tracking-wide text-emerald-300 shadow-[0_0_40px_-10px_rgba(16,185,129,0.7)] ${big ? 'px-6 py-3 text-base' : 'px-5 py-2.5 text-sm'}`} data-id="element-5" data-mp="1wuu1b5cfngpt">
      <ShieldCheck className={big ? 'h-6 w-6' : 'h-5 w-5'} data-id="element-6" data-mp="2kovfvb21p1pm" /> 14-Day Money-Back Guarantee
    </div>;
}

/* Moved OUT of the hero (2026-08-12). It was the first thing a visitor met on
   mobile -- two empty inputs asking them to do math before they knew what this
   was. 125 landing views produced zero CTA clicks with it gating the top of the
   page. It stays as a tool for people who scroll, not as the entry ask. */
function ProofStats() {
  const stats = [{
    icon: Inbox,
    stat: '< 1 in 6',
    body: 'unanswered calls to a contractor ever becomes a voicemail'
  }, {
    icon: Users,
    stat: '85%',
    body: 'of homeowners contact three or fewer contractors before hiring'
  }, {
    icon: TrendingDown,
    stat: '~40%',
    body: 'say they rarely or never hire a contractor who missed their first call'
  }];
  return <div className="mt-8 rounded-xl border border-hairline2 bg-ink p-6 sm:p-8" data-id="element-7" data-mp="387jk4qoabi64">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 sm:gap-4" data-id="element-8" data-mp="3d2yuv9qzwxyb">
        {stats.map(({
        icon: Icon,
        stat,
        body
      }) => <div key={body} className="flex items-start gap-3 sm:flex-col sm:items-start sm:gap-2" data-id="element-9" data-mp="2zbakpc0b4jjz">
            <Icon className="mt-0.5 h-4 w-4 flex-none text-emerald-500 sm:mt-0" strokeWidth={2} data-id="element-10" data-mp="345okdqm05cug" />
            <div data-id="element-11" data-mp="2pcuvxfor5mdn">
              <div className="text-2xl font-extrabold text-white" data-id="element-12" data-mp="2ue23bdmzgjjs">{stat}</div>
              <p className="mt-0.5 text-xs leading-snug text-neutral-400" data-id="element-13" data-mp="2ubxvwlpvl2er">{body}</p>
            </div>
          </div>)}
      </div>
      <p className="mt-5 border-t border-hairline2 pt-4 text-[11px] leading-relaxed text-neutral-500" data-id="element-14" data-mp="2kge4abicgpvt">
        Source: Service Direct's 2022 survey of 559 homeowners, and Service Direct's 2022 Call Performance Report.
        A homeowner picks from a short list and decides fast. Answering the phone isn't customer service, it's admission to that list.
      </p>
    </div>;
}

/* The low-commitment third door (2026-08-18 handoff): book and call are both
   high commitment; this is the raised hand for everyone not ready to talk to a
   human. MOVED to the bottom of the descent later the same day (Kevin's
   sequencing pass) -- it originally sat right after the pain section, asking
   for contact info before the visitor had seen the fix, the proof, or the
   price. Down there it's the safety net after the offer + guarantee, not a
   speed bump mid-argument. Same form, same webhook, nothing else changed.
   Full-width card, larger inputs, glow treatment, once-only scroll reveal. */
function SendNumbersForm() {
  const [name, setName] = useState('');
  const [business, setBusiness] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [ref, inView] = useInViewOnce(0.25);
  const reducedMotion = usePrefersReducedMotion();
  const revealed = reducedMotion || inView;
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;
    const lead = {
      name,
      business,
      phone,
      email,
      source: 'ai.viridiaanalytics.com missed-lead check',
      page: typeof window !== 'undefined' ? window.location.href : '',
      hp
    };

    // Honeypot: a human never fills this field. If it's non-empty, drop the
    // submit client-side without hitting the webhook -- the URL is public.
    if (hp) {
      setStatus('done');
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch(LEAD_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lead)
      });
      if (res.ok) {
        trackWebhookLead();
      } else {
        flagLeadDeliveryFailure('HTTP ' + res.status, lead);
      }
    } catch (err) {
      flagLeadDeliveryFailure(err instanceof Error ? err.message : 'network error', lead);
    }
    // Visitor always sees the success state -- a failed delivery is Kevin's
    // problem to chase via the flagged event/backup, not the visitor's to retry.
    setStatus('done');
  }
  const cardBase = `relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-inkAlt p-8 shadow-[0_20px_60px_-30px_rgba(16,185,129,0.4)] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] sm:p-10 ${revealed ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`;
  if (status === 'done') {
    return <div ref={ref} className={cardBase} data-id="element-15" data-mp="1ioz8b2bel3gk">
        <div className="pointer-events-none absolute inset-0 opacity-70" style={{
        background: 'radial-gradient(60% 80% at 50% 0%, rgba(15,176,125,0.10) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-16" data-mp="1ino3r0a4vroe" />
        <div className="relative text-center" data-id="element-17" data-mp="3ryym0z2way2s">
          <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-400" data-id="element-18" data-mp="3rw2o3ar1s9ec" />
          <h2 className="mt-4 text-2xl font-bold text-white" data-id="element-19" data-mp="3mx989boag6b0">Got it, we'll call you.</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-neutral-300" data-id="element-20" data-mp="3hyxyf0xztvss">
            Kevin will call you within one business day with a straight read on what missed calls are costing your shop.
          </p>
          <a href={BOOKING_URL} onClick={trackBookingOpened} className="mt-5 inline-block text-xs font-semibold text-emerald-400 underline decoration-emerald-700 underline-offset-4 hover:text-emerald-300" data-id="element-21" data-mp="387tinz0hu6ww">
            Or grab a time now instead
          </a>
        </div>
      </div>;
  }
  return <div ref={ref} className={cardBase} data-id="element-22" data-mp="2uc99hvakh32o">
      <div className="pointer-events-none absolute inset-0 opacity-70" style={{
      background: 'radial-gradient(60% 80% at 50% 0%, rgba(15,176,125,0.10) 0%, rgba(10,10,10,0) 70%)'
    }} data-id="element-23" data-mp="2pasciav7e0rd" />
      <div className="relative" data-id="element-24" data-mp="2km9guw6bj3sh">
        <div className="font-eyebrow text-[10px] font-semibold uppercase text-emerald-500" data-id="element-25" data-mp="2knkm70fewjlp">Not ready to call?</div>
        <h2 className="mt-2 text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-26" data-mp="26ocibu8b0nb6">Send me my numbers, I'll call you.</h2>
        <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-27" data-mp="2aqfxasu6ma17">
          Three fields. Kevin calls you back within one business day with a straight read on what missed calls are costing your shop.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 gap-4" data-id="element-28" data-mp="2plm076xzdsqb">
          <input type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Your name" aria-label="Your name" autoComplete="name" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-29" data-mp="2pjjcwuo7qdpa" />
          <input type="text" required value={business} onChange={e => setBusiness(e.target.value)} placeholder="Business name" aria-label="Business name" autoComplete="organization" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-30" data-mp="1aojfokrs2qsh" />
          <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" aria-label="Phone number" autoComplete="tel" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-31" data-mp="1fgkofwpqlzdr" />
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (optional)" aria-label="Email (optional)" autoComplete="email" className="w-full rounded-lg border border-hairline2 bg-ink px-5 py-4 text-base text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-32" data-mp="11p65tqzqs078" />
          {/* Honeypot -- visually hidden from sighted users, off the tab order, still
              reachable by a bot filling every field it finds. */}
          <input type="text" name="hp" value={hp} onChange={e => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }} data-id="element-33" data-mp="mmpmno1uxfwh" />
          <button type="submit" disabled={status === 'submitting'} className="mt-1 w-full rounded-lg bg-emerald-500 px-7 py-4 text-base font-bold text-black transition-colors hover:bg-emerald-400 disabled:opacity-60" data-id="element-34" data-mp="15ksaw0mkz8t8">
            {status === 'submitting' ? 'Sending…' : 'Send my numbers'}
          </button>
        </form>
        <p className="mt-4 text-center text-xs text-neutral-500" data-id="element-35" data-mp="1jcyiuzfky9hl">No spam, no drip campaign. Kevin calls you himself.</p>
      </div>
    </div>;
}

/* Case study section (item 6/7, 2026-08-18 handoff): the same two verified
   results the proof section used to carry as short blurbs, restructured as a
   story -- situation, what changed, the number -- because a story reads as
   evidence and a bare percentage reads as a claim. No client names, no
   screenshots, both per Kevin's standing anonymity rule.
   NOTE (2026-08-18, later same day): this section originally REPLACED the dare
   section. That was reversed -- Kevin confirmed the receptionist is reliable
   enough to hand to a cold stranger, so the dare is back above and these two now
   run together: the dare proves the product live, the case studies prove the
   outcome. Do not re-cut the dare without also cutting the hero's promise of it. */
function CaseStudyCard({
  eyebrow,
  situation,
  change,
  number
}: {
  eyebrow: string;
  situation: string;
  change: string;
  number: string;
}) {
  return <div className="rounded-lg border border-hairline2 bg-inkAlt p-6 sm:p-7" data-id="element-36" data-mp="2zy0qrsk1rzry">
      <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-37" data-mp="2v468mtk3bsuz">{eyebrow}</div>
      <div className="mt-5 space-y-4" data-id="element-38" data-mp="t8p7twyh1vew">
        <div className="flex gap-3" data-id="element-39" data-mp="y6qpqek5voos">
          <Wrench className="mt-0.5 h-4 w-4 flex-none text-neutral-500" strokeWidth={2} data-id="element-40" data-mp="y5p79p0tfzx1" />
          <p className="text-sm leading-relaxed text-neutral-400" data-id="element-41" data-mp="jh2nnb45om00"><span className="font-semibold text-neutral-300" data-id="element-42" data-mp="oh77nb49vyuz">The situation. </span>{situation}</p>
        </div>
        <div className="flex gap-3" data-id="element-43" data-mp="ahpdshc4dver">
          <Zap className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2} data-id="element-44" data-mp="agmq0ia3g932" />
          <p className="text-sm leading-relaxed text-neutral-400" data-id="element-45" data-mp="fhk5uo7msr1e"><span className="font-semibold text-neutral-300" data-id="element-46" data-mp="fe65flij2rwl">What changed. </span>{change}</p>
        </div>
        <div className="flex gap-3" data-id="element-47" data-mp="5icu25gjp4ge">
          <TrendingUp className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2} data-id="element-48" data-mp="5je6ad9arwzp" />
          <p className="text-sm leading-relaxed text-white" data-id="element-49" data-mp="1ux1625l84kdw"><span className="font-semibold" data-id="element-50" data-mp="1uv6h8g6u084j">The number. </span>{number}</p>
        </div>
      </div>
    </div>;
}
function CaseStudies() {
  const [ref, inView] = useInViewOnce(0.15);
  const reducedMotion = usePrefersReducedMotion();
  const revealed = reducedMotion || inView;
  const cards = [{
    eyebrow: 'Phoenix landscaping company',
    situation: 'A two-truck crew was missing about 1 in 4 calls, business as usual when there is more work than hands to answer the phone.',
    change: 'The AI answered every call and Yelp message instantly, day and night, and booked estimates straight onto the calendar without anyone touching a phone.',
    number: '284% more leads on the same ad spend, 28 estimates booked end to end by the AI, and a 39.95% close rate. Response time went from 4+ hours to instant.'
  }, {
    eyebrow: 'Residential and commercial painting company',
    situation: 'Close rate was strong whenever they actually reached a lead. The problem was reaching them before someone else did.',
    change: 'The AI took first response off their plate and booked its own estimates while the owner stayed on the tools running the jobs he already had.',
    number: '29 estimates booked in 30 days, about one a day, and $15,050 closed from estimates the AI booked hands-free.'
  }];
  return <section className="border-b border-hairline bg-inkAlt" data-id="element-51" data-mp="21mbzla3ggwa8">
      <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-52" data-mp="21oy9vhhqdpw5">
        <Eyebrow data-id="element-53" data-mp="26l5ftaa4n1e3">What this looks like for a shop like yours</Eyebrow>
        <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-54" data-mp="18ypyc8hpa3qj">
          Two real shops, before and after.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-55" data-mp="1dz3w8xpl7xau">
          Real past-client outcomes, anonymized. Not a guarantee of the same numbers for every business.
        </p>
        <div ref={ref} className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-56" data-mp="1nt48djjae2t2">
          {cards.map((c, i) => <div key={c.eyebrow} className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]" style={{
          transitionDelay: revealed ? `${i * 150}ms` : '0ms',
          opacity: revealed ? 1 : 0,
          transform: revealed ? 'translateY(0)' : 'translateY(24px)'
        }} data-id="element-57" data-mp="2udlvje6e6ha8" data-mpi={i}>
              <CaseStudyCard {...c} data-id="element-58" data-mp="dvcxlmf1963b" />
            </div>)}
        </div>
      </div>
    </section>;
}
const FLOW_SOURCES = ['Missed call', 'Yelp', 'Google', 'Facebook', 'Web form'];
const FLOW_STAGES = [{
  icon: Inbox,
  label: 'Lead captured',
  sub: 'Every call & message'
}, {
  icon: CalendarCheck,
  label: 'Booked',
  sub: 'On your calendar'
}, {
  icon: FileText,
  label: 'Invoice sent',
  sub: 'QuickBooks synced'
}, {
  icon: CheckCircle2,
  label: 'Job completed',
  sub: 'Tracked to done'
}, {
  icon: Star,
  label: 'Review requested',
  sub: 'Your reputation grows'
}];

/* Depth on the nodes (item 5, 2026-08-18): a soft directional glow + inset
   highlight instead of a flat fill, real easing instead of a linear transition. */
function FlowNode({
  stage,
  state
}: {
  stage: (typeof FLOW_STAGES)[number];
  state: 'idle' | 'current' | 'done';
}) {
  const Icon = stage.icon;
  const done = state === 'done';
  const current = state === 'current';
  return <div className="flex w-28 flex-none flex-col items-center text-center sm:w-32" data-id="element-59" data-mp="22ghkkf6i1qar">
      <div className="relative" data-id="element-60" data-mp="1xizm5iq3yth0">
        {current && <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-[vaPing_1.6s_ease-out_infinite]" data-id="element-61" data-mp="1xi6b45k49ht2" />}
        <div className={`relative flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${done ? 'border-emerald-500 bg-gradient-to-b from-emerald-400 to-emerald-600 text-black shadow-[0_4px_16px_-2px_rgba(16,185,129,0.55),inset_0_1px_0_rgba(255,255,255,0.3)]' : current ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400 shadow-[0_0_0_5px_rgba(16,185,129,0.08)]' : 'border-hairline2 bg-inkAlt text-neutral-600'}`} data-id="element-62" data-mp="1tei2rknnui6r">
          {done ? <Check className="h-5 w-5" strokeWidth={2.5} data-id="element-63" data-mp="3hcsw3iu0a3cx" /> : <Icon className="h-5 w-5" strokeWidth={2} data-id="element-64" data-mp="3lf5rpef1gkvz" />}
        </div>
      </div>
      <div className={`mt-3 text-sm font-semibold transition-colors duration-500 ${done || current ? 'text-white' : 'text-neutral-500'}`} data-id="element-65" data-mp="3q6ffe9h11e58">{stage.label}</div>
      <div className={`mt-0.5 text-[11px] leading-tight transition-colors duration-500 ${done || current ? 'text-neutral-400' : 'text-neutral-600'}`} data-id="element-66" data-mp="3v84qfp491rtb">{stage.sub}</div>
    </div>;
}

/* Travelling pulse instead of a hard fill (item 5): the track fills with a
   smoother, longer ease, and a small glowing dot travels along it once as it
   fills, then fades. Pure CSS keyframes, no library. */
function FlowConnector({
  filled,
  pulsing
}: {
  filled: boolean;
  pulsing: boolean;
}) {
  return <div className="flex items-center justify-center py-1 md:flex-1 md:px-1 md:py-0" data-id="element-67" data-mp="mpywnaf3uu5j">
      <div className="relative h-7 w-px overflow-hidden rounded-full bg-hairline2 md:mt-6 md:h-px md:w-full" data-id="element-68" data-mp="rnql1ugjjy4z">
        <div className={`absolute inset-0 origin-top rounded-full bg-gradient-to-b from-emerald-400 to-emerald-500 transition-transform duration-[750ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:origin-left md:bg-gradient-to-r ${filled ? 'scale-y-100 md:scale-x-100' : 'scale-y-0 md:scale-x-0'}`} data-id="element-69" data-mp="1zbgbbl6memk" />
        {pulsing && <>
            <span className="va-pulse-dot va-pulse-dot-v md:hidden" aria-hidden="true" data-id="element-70" data-mp="3fgu9faidwbtt" />
            <span className="va-pulse-dot va-pulse-dot-h hidden md:block" aria-hidden="true" data-id="element-71" data-mp="3ai0uddng8crj" />
          </>}
      </div>
    </div>;
}

/* Runs once on entering the viewport instead of looping forever (item 5): an
   IntersectionObserver starts a single staggered play-through the first time the
   diagram scrolls into view, then stops. Reduced motion jumps straight to the
   finished state, same as before. */
function LeadFlow() {
  const [active, setActive] = useState(-1);
  const [pulseIndex, setPulseIndex] = useState(-1);
  const [started, setStarted] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  useEffect(() => {
    const el = containerRef.current;
    if (!el || started) return;
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setStarted(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.35
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    if (reducedMotion) {
      setActive(FLOW_STAGES.length - 1);
      return;
    }
    const timers = FLOW_STAGES.map((_, i) => setTimeout(() => {
      setPulseIndex(i - 1);
      setActive(i);
    }, i * 550));
    const clearPulse = setTimeout(() => setPulseIndex(-1), FLOW_STAGES.length * 550 + 750);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(clearPulse);
    };
  }, [started, reducedMotion]);
  const capturing = active === 0;
  return <div ref={containerRef} className="relative mt-10 overflow-hidden rounded-xl border border-hairline2 bg-inkAlt p-6 sm:p-10" data-id="element-72" data-mp="fzxlw32jc6u3">
      <style data-id="element-73" data-mp="18qfn0yjp7y4v">{`
        @keyframes vaPing{0%{transform:scale(1);opacity:.5}70%,100%{transform:scale(2);opacity:0}}
        @keyframes vaPulseV{0%{transform:translateY(-100%);opacity:0}15%{opacity:1}85%{opacity:1}100%{transform:translateY(700%);opacity:0}}
        @keyframes vaPulseH{0%{transform:translateX(-100%);opacity:0}15%{opacity:1}85%{opacity:1}100%{transform:translateX(700%);opacity:0}}
        @keyframes vaGlow{0%,100%{opacity:.4}50%{opacity:.75}}
        .va-pulse-dot{position:absolute;width:10px;height:10px;border-radius:9999px;background:radial-gradient(circle,rgba(52,211,153,.95) 0%,rgba(52,211,153,0) 72%);pointer-events:none}
        .va-pulse-dot-v{left:50%;top:0;margin-left:-5px;animation:vaPulseV 700ms cubic-bezier(0.22,1,0.36,1) both}
        .va-pulse-dot-h{top:50%;left:0;margin-top:-5px;animation:vaPulseH 700ms cubic-bezier(0.22,1,0.36,1) both}
        .va-glow{animation:vaGlow 6s ease-in-out infinite}
        @media (prefers-reduced-motion: reduce){.va-pulse-dot{animation:none;display:none}.va-glow{animation:none}}
      `}</style>
      <div className="va-glow pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(55% 60% at 50% 38%, rgba(15,176,125,0.12) 0%, rgba(10,10,10,0) 72%)'
    }} data-id="element-74" data-mp="2dpb1vy12c4g0" />
      <div className="relative" data-id="element-75" data-mp="2mqsidhidu858">
        <div className="font-eyebrow text-center text-[10px] font-semibold uppercase text-neutral-500" data-id="element-76" data-mp="2mog4bffqwhxg">Leads come in from</div>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-id="element-77" data-mp="scrkayunpp67">
          {FLOW_SOURCES.map(s => <span key={s} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-500 ${capturing ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-hairline2 bg-ink text-neutral-500'}`} data-id="element-78" data-mp="xcdwd4yr0zdf">
              <span className={`h-1.5 w-1.5 rounded-full transition-colors duration-500 ${capturing ? 'bg-emerald-400' : 'bg-neutral-600'}`} data-id="element-79" data-mp="29ng62ors2pb7" />{s}
            </span>)}
        </div>
        <div className="mx-auto mt-3 h-5 w-px bg-gradient-to-b from-emerald-500/60 to-transparent" data-id="element-80" data-mp="2jkumiodpbe3i" />
        <div className="mt-2 flex flex-col items-center md:flex-row md:items-start md:justify-center" data-id="element-81" data-mp="2em9kcjew2kcf">
          {FLOW_STAGES.map((stage, i) => {
          const state: 'idle' | 'current' | 'done' = active > i ? 'done' : active === i ? 'current' : 'idle';
          return <React.Fragment key={stage.label} data-id="element-82" data-mp="2oe6zu2cxjygy" data-mpi={i}>
                <FlowNode stage={stage} state={state} data-id="element-83" data-mp="32cncsb1dyl7j" />
                {i < FLOW_STAGES.length - 1 && <FlowConnector filled={active > i} pulsing={pulseIndex === i} data-id="element-84" data-mp="2xdak39nu2jx1" />}
              </React.Fragment>;
        })}
        </div>
        <div className="mt-8 text-center text-xs text-neutral-500" data-id="element-85" data-mp="3gw7z4q072sb4">One system runs the whole job, start to finish. You just show up and do the work.</div>
      </div>
    </div>;
}

/* THE DARE -- restored 2026-08-18 after Kevin confirmed the receptionist is
   reliable enough to hand to a cold stranger. It was cut earlier the same day
   (the case-study section took its slot) while the hero kept promising it:
   "Call right now and our AI will answer. That's the whole product, live."
   That left the page making a claim it no longer backed.
   Restored verbatim from the version that was actually live (artifact aa891d60,
   v10), NOT from v9 -- v9's test-call pull-quote was deliberately removed in
   v10 and stays removed. Only change: the CTA fires trackCallTapped, not the
   old trackLead, so a dare tap stays honest intent and never counts as a
   converted Lead. */
function TheDare() {
  return <section className="relative overflow-hidden border-b border-hairline bg-inkAlt" data-id="element-86" data-mp="2zq1ohrhaobth">
      <div className="pointer-events-none absolute inset-0 opacity-70" style={{
      background: 'radial-gradient(60% 70% at 50% 50%, rgba(15,176,125,0.14) 0%, rgba(10,10,10,0) 70%)'
    }} data-id="element-87" data-mp="2znyxp58bienp" />
      <div className="relative mx-auto max-w-3xl px-5 py-16 text-center sm:py-24" data-id="element-88" data-mp="2pq121wo4pfl2">
        <Eyebrow data-id="element-89" data-mp="2krzjdcum7ia4">Don't take our word for it</Eyebrow>
        <h2 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl" data-id="element-90" data-mp="2gqxhspd86u4z">
          Think AI won't close?<br className="hidden sm:block" data-id="element-91" data-mp="2bzfswzoh8k0d" /> Call it right now.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-neutral-300" data-id="element-92" data-mp="2714gqiaplxez">
          The number below isn't a sales line. It's the exact system we'd put on your phone,
          answering live. Try to stump it. Ask what it costs. Ask if it's a robot.
        </p>

        <div className="mx-auto mt-8 max-w-md" data-id="element-93" data-mp="1x514ukq22keu">
          <a href={PHONE_TEL} onClick={trackCallTapped} className="group flex w-full items-center justify-center gap-3 rounded-md bg-emerald-500 px-7 py-5 text-lg font-extrabold tracking-tight text-black transition-colors hover:bg-emerald-400" data-id="element-94" data-mp="1t2o1g2ys6yl8">
            <PhoneCall className="h-5 w-5 flex-none" data-id="element-95" data-mp="hlaar1egam9q" />
            Call {PHONE_DISPLAY}
          </a>
          <p className="mt-3 text-xs text-neutral-500" data-id="element-96" data-mp="miagfb7e1n0b">
            Answers in seconds, 24/7, same as it would for your customers.
          </p>
        </div>

        <p className="mx-auto mt-10 max-w-lg text-sm leading-relaxed text-neutral-400" data-id="element-97" data-mp="3q0244dehrksp">
          That's what picks up at 9pm when you're covered in drywall dust. Not voicemail.
        </p>
      </div>
    </section>;
}
export function App() {
  return <div className="min-h-full w-full bg-ink text-neutral-100" data-id="element-98" data-mp="3m28fzmzqy1b">
      {/* THE DESCENT (Kevin, 2026-08-18: "this is a funnel... it needs to catch
          them again, keep them interested all the way down.") Section order is a
          designed argument, not a stack of cards:
            1  HERO        the promise + zero risk, both CTAs
            2  PAIN        mirror (scenarios) -> evidence (stats) -> escalation (reputation)
            3  FIX         the mechanism: flow diagram, features, the 11pm outcome
            4  BETTER      for owners already running ads: bigger jobs, not more noise
            5  DARE        skepticism peaks right after mechanism -> hand them the phone
            6  CASES       the dare proves it works live; these prove it pays
            7  WHO         credibility right before the ask
            8  OFFER       price + value stack + the 15,050 math
            9  GUARANTEE   the answer to the fear the price just created
           10  SAFETY NET  the low-commitment form, catching everyone who won't book
           11  CLOSER      belief + last CTAs
          Reorder only with the whole argument in mind: a section that's right in
          isolation can still be wrong in sequence. The form used to sit at #3,
          asking for contact info before the visitor knew what we do; the
          guarantee used to sit three screens above the price it exists to
          defend. */}

      {/* 1. HERO -- reverted 2026-08-18 (Kevin's own review, overruling the prior
             "$15,050" hero). His reasoning: this buyer has already heard every
             money-back, cancel-anytime pitch there is, so the first thing on the
             page has to disarm, not impress. Subhead tuned to risk removal, not
             capability. Message-match: per the 2026-08-18 first-hand
             Meta snapshot, "Ad - pain" is the ONLY active ad and matches this H1
             verbatim; stat proof / guarantee / one-system are all paused. Current
             truth lives in ads-team/campaigns/_performance-latest.md + FUNNEL-SPEC,
             never in this comment. */}
      <header className="relative overflow-hidden border-b border-hairline" data-id="element-99" data-mp="22avk9dgy49ns">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{
        background: 'radial-gradient(60% 50% at 50% 0%, rgba(15,176,125,0.16) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-100" data-mp="1xfg94taborv2" />
        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 pb-14 pt-10 text-center sm:pb-18 sm:pt-14" data-id="element-101" data-mp="100li25d1zvag">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-16 w-auto sm:h-24" data-id="element-102" data-mp="13zuejhzha79p" />
          <div className="mt-8" data-id="element-103" data-mp="18tf2eh7cxkv6"><Eyebrow data-id="element-104" data-mp="18tyma3gkijbu">Phoenix Home-Service Owners</Eyebrow></div>
          <h1 className="mt-4 text-[2.2rem] font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl" data-id="element-105" data-mp="1dpvshgy4a9aw">
            We answer every lead, or you don't pay.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-106" data-mp="1nol6gymw79lv">
            Every call, text, and Yelp lead answered within seconds, day or night. If one slips through
            during your first 14 days, that month is on us. No fine print, no hoops to jump through.
          </p>

          <div className="mt-8 grid w-full max-w-md grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-107" data-mp="3tj4e4lcrkqjn">
            <BookCTA label="Book Your Free 15-Minute Call" full data-id="element-108" data-mp="3lyttonn8s5cv" />
            <CallCTA full data-id="element-109" data-mp="3h7txxlpuztby" />
          </div>
          <p className="mt-3 text-xs text-neutral-500" data-id="element-110" data-mp="3h6skjaldeur2">
            Call right now and our AI will answer. That's the whole product, live.
          </p>

          <div className="mt-7" data-id="element-111" data-mp="3c6efm1f2g0x9"><GuaranteeBadge big data-id="element-112" data-mp="387p2m9xxwle5" /></div>
        </div>
      </header>

      {/* 2. THE PAIN -- mirror first, evidence second (reordered 2026-08-18,
             Kevin's descent pass): the four scenario cards used to sit BELOW the
             stats box. A stranger's statistics don't land until the reader has
             seen himself in the scene, so now it's scenes -> stats -> the
             reputation escalation. */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-113" data-mp="3qwllzirstthl">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:py-20" data-id="element-114" data-mp="2khkvvtpgg5ix">
          <Eyebrow data-id="element-115" data-mp="2kiw3y5pxnzfu">What a missed call actually costs you</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-116" data-mp="2fhggy8473iut">
            The caller doesn't leave a voicemail. They just call the next name on Google.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-117" data-mp="c1jvi3qe0a75">
            A busy shop misses 1 out of every 4 calls. At a few thousand dollars a job, that's tens of
            thousands walking out the door every month, leads you already paid Google, Yelp, and Meta to send you.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-118" data-mp="q1ku1cj6j5jg">
            {["You're on a rooftop swapping a condenser and the phone buzzes in your pocket.", "You're under a sink mid-repair with wet hands and no way to grab it.", "You're driving between jobs and it goes straight to voicemail.", "You're mid-estimate with one customer while a second lead is calling right now."].map((line, i) => <div key={i} className="rounded-lg border border-hairline bg-ink p-5 text-sm leading-relaxed text-neutral-400" data-id="element-119" data-mp="36pyi3gp9w02d" data-mpi={i}>{line}</div>)}
          </div>
          <ProofStats data-id="element-120" data-mp="2u6baex4yw5sh" />

          {/* REPUTATION -- replaced the cost calculator (Kevin, 2026-08-18):
                 "get rid of the calculator... we need to hit pain, reduce risk,
                 sell the outcome. Reputation is marketing, not your cool logo or
                 vehicle wrapping." The calculator was homework; this is the
                 knife. */}
          <div className="mt-10 rounded-xl border border-emerald-500/25 bg-ink p-7 sm:p-9" data-id="element-121" data-mp="2fhoo2bgxadrr">
            <Eyebrow data-id="element-122" data-mp="2ak57v60nzqx6">Reputation is marketing</Eyebrow>
            <h3 className="mt-3 text-xl font-bold leading-snug text-white sm:text-2xl" data-id="element-123" data-mp="2p7q6ammlh1yr">
              A missed call costs you the job. A missed appointment costs you your name.
            </h3>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-124" data-mp="1wqdv7ormzqx8">
              Thirty quiet minutes is all it takes. No answer, not even a missed-call text, and that
              $10,000 job is already talking to the next name on the list. You didn't lose it on price
              or on quality. You lost it in the half hour you couldn't reach your phone.
            </p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-125" data-mp="5er9fahhvzei">
              And the appointment that got booked and forgotten? That's not a missed job anymore.
              That's a one-star review, doing your marketing for you, to every neighbor who searches
              your name for the next five years.
            </p>
            <p className="mt-5 max-w-2xl text-base font-semibold leading-relaxed text-white" data-id="element-126" data-mp="3duudl69ef5y3">
              Your reputation <span className="text-emerald-400" data-id="element-127" data-mp="3hul4rb2u7o35">is</span> your marketing. Not the logo.
              Not the truck wrap. What homeowners say about you when you're not in the room.
            </p>
          </div>
        </div>
      </section>

      {/* 3. THE FIX */}
      <section className="border-b border-hairline" data-id="element-128" data-mp="123qigrpwl57r">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-129" data-mp="126kym71vw3vd">
          <Eyebrow data-id="element-130" data-mp="1c86wrgnqie9g">From missed call to five-star review</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-131" data-mp="179c29hgen19i">
            One lead comes in. The system runs the entire job for you.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-132" data-mp="yhjujeirm68m">
            Every call, text, and message from every platform lands in one place, and the system carries
            it the whole way: booked, invoiced, completed, and reviewed. Nothing slips.
          </p>
          <LeadFlow data-id="element-133" data-mp="jk1b8rouwl8k" />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-134" data-mp="jjhu3d7160vl">
            {[{
            icon: Bot,
            title: 'Instant response to every lead',
            body: 'An AI receptionist answers every call, text, and message in seconds, 24/7, before they dial the next name.'
          }, {
            icon: Inbox,
            title: 'One unified inbox',
            body: 'Every call, text, Yelp, Google, and Facebook message lands in one place, not five apps.'
          }, {
            icon: LayoutGrid,
            title: 'Branded CRM + mobile app',
            body: 'Run the whole thing from your phone. It chases every lead until they book or say no.'
          }, {
            icon: Star,
            title: 'Reviews + QuickBooks-synced invoicing',
            body: 'Reputation and invoicing run in the background while you run the job. Google Business Profile set up and optimized.'
          }].map(({
            icon: Icon,
            title,
            body
          }, i) => <div key={i} className="rounded-lg border border-hairline2 bg-inkAlt p-6" data-id="element-135" data-mp="29jc6i2cvxrd9" data-mpi={i}>
                <Icon className="h-5 w-5 text-emerald-500" strokeWidth={2} data-id="element-136" data-mp="2ehm1abup6pyc" />
                <h3 className="mt-4 text-base font-semibold text-white" data-id="element-137" data-mp="20huq0ivkfno1">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-138" data-mp="25eup29tzzq5n">{body}</p>
              </div>)}
          </div>
          {/* THE 11PM STORY (Kevin, 2026-08-18): "our clients are waking up to
                 appointments in their calendar that got booked at 11pm." Sell the
                 outcome, not the feature list. */}
          <div className="mt-10 rounded-xl border border-hairline2 bg-inkAlt p-6 sm:p-8" data-id="element-139" data-mp="dptvbtzsdwc2">
            <Eyebrow data-id="element-140" data-mp="8r0h1zcoe1au">What that looks like on a Tuesday night</Eyebrow>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-300" data-id="element-141" data-mp="mlbbmxbcspot">
              <span className="font-semibold text-white" data-id="element-142" data-mp="htjkakkdifxm">11:04pm.</span> A homeowner's water heater lets
              go. They call the first three names on Google. Two ring to voicemail.
              <span className="font-semibold text-white" data-id="element-143" data-mp="rk4g608nx6cu"> Yours answers on the second ring</span>, walks
              them through it, and books the estimate. <span className="font-semibold text-white" data-id="element-144" data-mp="15iktia115v3y">11:07pm,
              it's on your calendar.</span> You find out at breakfast.
            </p>
            <p className="mt-4 max-w-2xl text-base font-semibold leading-relaxed text-white" data-id="element-145" data-mp="37a7e9tubwtrz">
              Our clients wake up to jobs that booked themselves overnight. No call-back list. No
              shooting from the hip. That's the whole point.
            </p>
          </div>
          <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-146" data-mp="1djd59cq3b7rq">
            <BookCTA label="Book a 15-Min Call" data-id="element-147" data-mp="1rimludi7k3w8" />
            <CallCTA data-id="element-148" data-mp="1rkfu1x8gyw6c" />
          </div>
        </div>
      </section>

      {/* 4. BETTER LEADS (Kevin, 2026-08-18): "we can OPTIMIZE those ads for
             higher ticket, instead of being blown up with 6-7 leads for small
             ticket diagnostics... thats also why they get too many leads." This
             also qualifies out the $100-answering-service shopper: it speaks to
             an owner who already spends on ads and wants bigger jobs, not a
             cheaper phone bill. */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-149" data-mp="268aopj61fam2">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:py-20" data-id="element-150" data-mp="2k70lexoy389w">
          <Eyebrow data-id="element-151" data-mp="2k57c1amy69y7">Better leads, not just more leads</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-152" data-mp="2f9rykjsuqg2b">
            Stop drowning in $150 service calls.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-153" data-mp="33t3l9h2wjwdw">
            Already running Yelp or Google ads? Then you know the other problem: the phone blows up
            with six wasp nests, a one-room paint quote, and a lawn cleanup, while the $12,000 install
            goes to somebody else. That's not winning. That's noise with an ad bill attached.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-154" data-mp="2ku7pi1beb9xy">
            <div className="rounded-lg border border-hairline2 bg-ink p-6" data-id="element-155" data-mp="2kwkhl7avb0oq">
              <TrendingUp className="h-5 w-5 text-emerald-500" strokeWidth={2} data-id="element-156" data-mp="26765hrfwq55n" />
              <h3 className="mt-4 text-base font-semibold text-white" data-id="element-157" data-mp="2b5prfrui3gy0">We tune your ads toward high-ticket work</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-158" data-mp="1x5f1qs4xg8ef">
                Same spend, pointed at the $8,000&ndash;$15,000 installs and remodels instead of
                small-ticket diagnostics. Fewer leads, bigger jobs, calmer phone.
              </p>
            </div>
            <div className="rounded-lg border border-hairline2 bg-ink p-6" data-id="element-159" data-mp="2w9k8wswvsax">
              <CalendarCheck className="h-5 w-5 text-emerald-500" strokeWidth={2} data-id="element-160" data-mp="3u2kqd7cs3j94" />
              <h3 className="mt-4 text-base font-semibold text-white" data-id="element-161" data-mp="3q20v69brmppm">And every one of them gets answered</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-162" data-mp="qn5iocrvhlr5">
                The right lead is worthless if it rings out. Each one is answered in seconds and chased
                until it books or says no. Right leads in, nothing slips out.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. THE DARE -- sits right after the mechanism, where skepticism peaks:
             the visitor has just been told what the system does, so this is the
             moment to hand them the phone instead of arguing. */}
      <TheDare data-id="element-163" data-mp="2xfg3t8vaidm2" />

      {/* 6. CASE STUDIES -- the dare proves the product works live, these prove
             it produced an outcome. Both, not either. */}
      <CaseStudies data-id="element-164" data-mp="1huxb05usk1mg" />

      {/* 7. WHO'S BEHIND IT -- credibility right before the ask (moved above the
             offer in the 2026-08-18 descent pass). Trimmed earlier the same day:
             the two client results live in the case-study section as full
             stories, so this carries the credibility line, trust badge, and CTA
             without repeating the numbers. */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-165" data-mp="20jkfon922ozi">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-166" data-mp="25jq8i6r2ahj6">
          <Eyebrow data-id="element-167" data-mp="25khucpuux0w9">Who's behind this</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-168" data-mp="29lton8ooeaas">
            Run by people who've done this before, not a call center script.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-169" data-mp="f70uzf7crtlh">
            Built by former Yelp reps who managed over $2 million a month in ad spend for home-service businesses.
          </p>

          <div className="mt-10 flex flex-col items-center gap-4" data-id="element-170" data-mp="3c1g2bcff9xc2">
            <div className="flex flex-col gap-3 sm:flex-row" data-id="element-171" data-mp="3c3986pi7odj0">
              <BookCTA label="Get this on your calendar" data-id="element-172" data-mp="3pzd2eit34lvh" />
              <CallCTA data-id="element-173" data-mp="3l1upyifuzoiq" />
            </div>
            <div className="flex flex-wrap items-center gap-6 opacity-90" data-id="element-174" data-mp="3l24fk4wzrtnw">
              <img src={CHAMBER_BADGE} alt="Scottsdale Chamber of Commerce" className="h-9 w-auto" data-id="element-175" data-mp="2xb8urrqydyq5" />
            </div>
          </div>
        </div>
      </section>

      {/* 8. OFFER -- item 8's promotional-rate change was cancelled by Kevin the
             same day (commit f072d19, "dont do the 897 thing") before this run
             read the handoff. Pricing stays exactly as it was: plain $1,197/month,
             no strikethrough, no founding-rate framing, no scarcity language
             attached to price. The 5-businesses-a-month line stays a capacity
             statement further down, not a pricing deadline. */}
      <section className="border-b border-hairline" data-id="element-176" data-mp="p2u3behbrkr4">
        <div className="mx-auto max-w-2xl px-5 py-14 text-center sm:py-20" data-id="element-177" data-mp="tvnbkcc05b7y">
          <Eyebrow data-id="element-178" data-mp="g3p92kcsqdkr">The offer</Eyebrow>
          <h2 className="mt-3 text-4xl font-extrabold text-white sm:text-5xl" data-id="element-179" data-mp="g4qkipxq5230">$1,197<span className="text-xl font-semibold text-neutral-400" data-id="element-180" data-mp="k5108ubbj6v2">/month</span></h2>
          <p className="mt-2 text-sm font-medium text-emerald-500" data-id="element-181" data-mp="17uvdhczjuxfu">Month to month. No contract. Cancel anytime.</p>

          <div className="mx-auto mt-7 max-w-md rounded-xl border border-hairline2 bg-inkAlt p-6 text-left" data-id="element-182" data-mp="1ctptk764u7w9">
            <p className="text-center text-sm font-semibold text-white" data-id="element-183" data-mp="yuq8f9htwb4u">Why that number is a steal:</p>
            <ul className="mt-4 space-y-2.5" data-id="element-184" data-mp="13si9at0gb7l6">
              {['Replaces a full-time receptionist ($3,000+/mo). The AI answers every call, 24/7.', 'Replaces your CRM, review tool, and invoicing. One bill instead of five apps.', 'Catches the calls you already paid Google, Yelp & Meta to send you.', 'Built and set up with you, in your voice. Live in days, not months.'].map(li => <li key={li} className="flex items-start gap-2 text-sm leading-relaxed text-neutral-300" data-id="element-185" data-mp="1kdh8xor3rz6f">
                  <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2.5} data-id="element-186" data-mp="1gbvrdq3miwpw" />{li}
                </li>)}
            </ul>
            <p className="mt-4 border-t border-hairline2 pt-4 text-center text-sm font-bold text-emerald-400" data-id="element-187" data-mp="1bednie4gr7or">
              One client turned $1,197 into $15,050 in thirty days, from estimates the AI booked on its
              own. One missed $3,000 job costs more than a month of this. Why would you let that go?
            </p>
          </div>

          <div className="mt-6" data-id="element-188" data-mp="2vse0p6eo92hh"><GuaranteeBadge data-id="element-189" data-mp="2vsxkksnvu0y5" /></div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-190" data-mp="2vruswiiqjuh6">
            <span className="inline-flex items-center gap-1.5" data-id="element-191" data-mp="2roy8cm9mxoal"><Clock className="h-3.5 w-3.5 text-emerald-500" data-id="element-192" data-mp="di8c7viv8lzs" /> Live in days, not months</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-193" data-mp="ihkwq9m9lgz7"><Users className="h-3.5 w-3.5 text-emerald-500" data-id="element-194" data-mp="ig9tqc0n8d95" /> Only 5 new businesses onboarded per month</span>
          </div>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-195" data-mp="rag7fiui44wd">
            <BookCTA label="Book Your Free 15-Minute Call" data-id="element-196" data-mp="3qq30pk3c1212" />
            <CallCTA data-id="element-197" data-mp="3qrnwfd1dur0i" />
          </div>
        </div>
      </section>

      {/* 9. GUARANTEE -- deliberately AFTER the offer (2026-08-18 descent pass):
             the price creates the fear, this answers it on the very next scroll.
             It used to sit three screens above the price, where it defended
             nothing. Names the mechanisms explicitly; scope (response speed
             only, timestamped and logged) and the delivery carve-out unchanged. */}
      <section className="border-b border-hairline" data-id="element-198" data-mp="1jyc1sgwllkpz">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:py-20" data-id="element-199" data-mp="1k16hxw8kwjdl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10" data-id="element-200" data-mp="vn1l1fhmylax">
            <ShieldCheck className="h-7 w-7 text-emerald-500" data-id="element-201" data-mp="14ev8llvl0wa0" />
          </div>
          <h2 className="mt-5 text-2xl font-extrabold leading-snug text-white sm:text-3xl" data-id="element-202" data-mp="14gy2igzuv9k0">
            We respond to every missed call in your first 14 days, or your first month is 100% money back.
          </h2>
          <div className="mx-auto mt-6 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-203" data-mp="19egg4koticyc">
            {[{
            icon: Zap,
            text: 'Auto-responders fire for every lead'
          }, {
            icon: Bot,
            text: 'AI receptionist configured for missed calls'
          }, {
            icon: MessageSquare,
            text: 'Missed-call text-back'
          }].map(({
            icon: Icon,
            text
          }) => <div key={text} className="flex items-center gap-2.5 rounded-lg border border-hairline2 bg-inkAlt p-4 sm:flex-col sm:items-center sm:gap-2 sm:text-center" data-id="element-204" data-mp="3p75nqjgxqpyc">
                <Icon className="h-4 w-4 flex-none text-emerald-500 sm:h-5 sm:w-5" strokeWidth={2} data-id="element-205" data-mp="3b0c3b50g3gzc" />
                <span className="text-xs font-medium leading-snug text-neutral-300" data-id="element-206" data-mp="3f0w3khgbf0pp">{text}</span>
              </div>)}
          </div>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-neutral-400" data-id="element-207" data-mp="368u2qi2c3iew">
            We guarantee the one thing we fully control: missed-call response speed. Every response is
            timestamped and logged. If our system does not respond to every missed call within 5 minutes
            during your first 14 days, your first month is refunded, no hassle. See full terms below.
          </p>
        </div>
      </section>

      {/* 10. THE SAFETY NET -- the low-commitment door, moved from slot 3 in the
             2026-08-18 descent pass. Catches everyone the offer + guarantee
             convinced but didn't push to book or dial. */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-208" data-mp="li3cfzgpd83y">
        <div className="mx-auto max-w-xl px-5 py-16 sm:py-24" data-id="element-209" data-mp="1nbajvxivk614">
          <SendNumbersForm data-id="element-210" data-mp="1s9lsk4xfoahr" />
        </div>
      </section>

      {/* 11. CLOSER */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-211" data-mp="1s7hmbgc2azeb">
        <div className="mx-auto max-w-2xl px-5 py-20 text-center sm:py-28" data-id="element-212" data-mp="1x7x18cbzseye">
          <h2 className="text-3xl font-extrabold leading-tight text-white sm:text-4xl" data-id="element-213" data-mp="21zng8vlvjqw5">You're doing everything right. Stop letting the phone undo it.</h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-214" data-mp="26339rj2ndb4m">
            You run the ads. You run the crew. You do the work. The only leak in the whole operation
            is the thirty minutes nobody could answer, and that's the one thing we fix completely.
            From first ring to five-star review, covered. Book fifteen minutes, or call right now and
            try to stump it.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-215" data-mp="qjqeg6sxtchi">
            <BookCTA label="Book Your Call" data-id="element-216" data-mp="33u9sfg0auqu3" />
            <CallCTA data-id="element-217" data-mp="33w2wqonfwyyx" />
          </div>
          <div className="mt-6 flex justify-center" data-id="element-218" data-mp="2z3tdtskqsz65"><GuaranteeBadge data-id="element-219" data-mp="2z2qk7cw1cimj" /></div>
        </div>
      </section>

      {/* TERMS */}
      <section id="terms" className="border-b border-hairline" data-id="element-220" data-mp="2v1etf41fdrcr">
        <div className="mx-auto max-w-3xl px-5 py-12" data-id="element-221" data-mp="2uztw56nq7ub7">
          <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-222" data-mp="2q4el0mh3scih">Guarantee terms &amp; conditions</div>
          <div className="mt-3 space-y-2 text-[11px] leading-relaxed text-neutral-500" data-id="element-223" data-mp="3mr9s3rb6ate3">
            <p data-id="element-224" data-mp="3isk5r985q1ib">The 14-day money-back guarantee applies to the first month only and to missed-call response speed only. It guarantees that Viridia's system sends a response to every missed call it receives within 5 minutes during the client's first 14 days after go-live. Response times are automatically timestamped and logged; those logs are the sole record for any guarantee claim.</p>
            <p data-id="element-225" data-mp="1xcuz2ut4gkcs">The guarantee covers missed-call response and delivery, which Viridia controls. It does not guarantee booked jobs, revenue, close rate, or number of customers, which depend on the client's own pricing, sales, and market. The 14-day window begins at go-live, once the system is active and the client has completed required onboarding.</p>
            <p data-id="element-226" data-mp="1utrl8su0asb3">The guarantee does not apply if the client has not completed onboarding or synced their calendar or granted required access, disconnects or revokes their phone, Yelp, Google, or CRM integrations, or pauses, disables, or routes calls outside the system. One claim per client; refund of the first month's fee is the sole and entire remedy. Any one-time setup fee is non-refundable.</p>
            <p data-id="element-227" data-mp="1b5r4e68xtjko">The guarantee covers responses the system sends. It does not apply where a response cannot be delivered for reasons outside Viridia's control. This includes where the caller or lead has opted out of messages, unsubscribed, blocked or reported the number, or withdrawn, archived, cancelled, or reclaimed their request before a response could be delivered, and where a carrier, lead platform, or messaging provider blocks, filters, or delays delivery. Leads in any of those categories are excluded from the guarantee calculation.</p>
            <p data-id="element-228" data-mp="sjbbdsxryc62">Service is month to month; cancel anytime with 7 days' notice. The system relies on third-party platforms (the client's phone carrier, Yelp, Google, Meta, and workflow tools); Viridia is not responsible for their outages or policy changes, and the guarantee is contingent on those integrations remaining connected. Client results shown are real past-client outcomes and are not a guarantee of the same results.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 py-10" data-id="element-229" data-mp="16ag5ygmace9j">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center" data-id="element-230" data-mp="16bhhsnb4l4sb">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-12 w-auto opacity-80" data-id="element-231" data-mp="1k6th78087jfk" />
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-232" data-mp="1g580fbkkml03">
            <a href={PHONE_TEL} onClick={trackCallTapped} className="inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200" data-id="element-233" data-mp="39o77qrp6brse"><Phone className="h-3.5 w-3.5" data-id="element-234" data-mp="3jfbe530sa4bm" /> {PHONE_DISPLAY}</a>
            <span className="inline-flex items-center gap-1.5" data-id="element-235" data-mp="3ji7awo0wamyh"><Mail className="h-3.5 w-3.5" data-id="element-236" data-mp="3odwc0vskcbww" /> info@viridiaanalytics.com</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-237" data-mp="2qrp9rsjqevnp"><MapPin className="h-3.5 w-3.5" data-id="element-238" data-mp="2qt0djsd6g3et" /> Phoenix, AZ</span>
          </div>
          <a href="https://viridiaanalytics.com" target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-600 underline decoration-neutral-800 underline-offset-4 hover:text-neutral-400" data-id="element-239" data-mp="2vr1uo7r1lsnn">See the full Viridia Analytics site</a>
        </div>
      </footer>
    </div>;
}